import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SEO } from '../../components-new/SEO/SEO';
import { AnimatedSection } from '../../components-new/AnimatedSection/AnimatedSection';
import { ServiceCard } from '../../components-new/ServiceCard/ServiceCard';
import { ServiceModal } from '../../components-new/ServiceModal/ServiceModal';
import { CampaignConditions } from '../../components-new/CampaignConditions/CampaignConditions';
import { services, packages } from '../../data/services';
import './style.less';

export const Services = () => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);

  const scrollToContent = () => {
    const heroElement = document.querySelector('.page-hero');
    if (heroElement) {
      const heroBottom = heroElement.getBoundingClientRect().bottom + window.scrollY;
      window.scrollTo({ top: heroBottom, behavior: 'smooth' });
    }
  };

  const handleServiceClick = (service, translationKey) => {
    const serviceData = {
      id: service.id,
      icon: service.icon,
      title: t(`services.${translationKey}.title`),
      price: t(`services.${translationKey}.price`),
      duration:
        t(`services.${translationKey}.duration`) !== `services.${translationKey}.duration`
          ? t(`services.${translationKey}.duration`)
          : null,
      description: t(`services.${translationKey}.description`),
    };

    // Add optional fields if they exist
    const intro = t(`services.${translationKey}.intro`, { defaultValue: '' });
    if (intro) serviceData.intro = intro;

    const priceNote = t(`services.${translationKey}.priceNote`, { defaultValue: '' });
    if (priceNote) serviceData.priceNote = priceNote;

    // Handle includes array
    const includesKey = `services.${translationKey}.includes`;
    const includes = t(includesKey, { returnObjects: true, defaultValue: null });
    if (includes && Array.isArray(includes)) {
      serviceData.includes = includes;
    }

    // Handle price details
    const priceDetailsBase = t(`services.${translationKey}.priceDetails.base`, {
      defaultValue: '',
    });
    if (priceDetailsBase) {
      serviceData.priceDetails = {
        base: priceDetailsBase,
        additional: t(`services.${translationKey}.priceDetails.additional`, {
          returnObjects: true,
          defaultValue: [],
        }),
        note: t(`services.${translationKey}.priceDetails.note`, { defaultValue: '' }),
      };
    }

    setSelectedService(serviceData);
  };

  return (
    <div className="services-page">
      <SEO
        title={t('services.seo.title')}
        description={t('services.seo.description')}
        keywords={t('services.seo.keywords')}
      />
      {/* Hero Section */}
      <section
        className="page-hero"
        style={{ backgroundImage: `url('/images/projects/project1/livingroom.jpg')` }}
      >
        <div className="hero-overlay" aria-hidden="true"></div>
        <div className="container">
          <AnimatedSection className="hero-content">
            <span className="hero-subtitle">{t('services.heroSubtitle', 'Какво предлагаме')}</span>
            <h1>{t('services.title')}</h1>
            <p>{t('services.subtitle')}</p>
            <div className="hero-line" aria-hidden="true"></div>
          </AnimatedSection>
        </div>
        <button
          className="hero-scroll-indicator"
          onClick={scrollToContent}
          aria-label={t('services.scrollToServices', 'Превъртете към услугите')}
          tabIndex={0}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            aria-hidden="true"
          >
            <i className="fas fa-chevron-down" aria-hidden="true"></i>
          </motion.div>
        </button>
      </section>

      <section className="section packages-section">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">{t('services.packages.title')}</h2>
          </AnimatedSection>

          <div className="packages-grid">
            {packages.map((pkg, index) => (
              <ServiceCard
                key={pkg.id}
                serviceId={pkg.id}
                icon={pkg.icon}
                title={t(`services.${pkg.translationKey}.title`)}
                price={t(`services.${pkg.translationKey}.price`)}
                description={t(`services.${pkg.translationKey}.shortDescription`)}
                delay={index * 0.1}
                onClick={() => handleServiceClick(pkg, pkg.translationKey)}
                isPackage={true}
              />
            ))}
          </div>

          <CampaignConditions />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">{t('services.individual.title')}</h2>
          </AnimatedSection>

          <div className="services-grid">
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                serviceId={service.id}
                icon={service.icon}
                title={t(`services.${service.translationKey}.title`)}
                price={t(`services.${service.translationKey}.price`)}
                duration={
                  t(`services.${service.translationKey}.duration`) !==
                  `services.${service.translationKey}.duration`
                    ? t(`services.${service.translationKey}.duration`)
                    : null
                }
                description={t(`services.${service.translationKey}.shortDescription`)}
                delay={index * 0.1}
                onClick={() => handleServiceClick(service, service.translationKey)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
      )}
    </div>
  );
};
